<template>
  <div>
    <div class="panel-heading">
      <h4 class="panel-title">{{ $t('admin.settingsTitle') }}</h4>
    </div>
    <form @submit.prevent="submitForm()">
      <v-alert type="success" :value="successAlert">
        {{ $t('admin.settingsUpdatedSuccessfully') }}
      </v-alert>
      <v-alert type="error" :value="errorAlert">
        {{ $t('admin.pleaseUpdateAnything') }}
      </v-alert>
      <v-card class="settings-section" flat outline>
        <v-card-title class="settings-section-header">
          <h5 class="settings-section-title">{{ $t('admin.securitySection') }}</h5>
          <p class="settings-section-desc">{{ $t('admin.securitySectionDesc') }}</p>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-switch
                  v-model="form.secret_key_enabled"
                  :label="$t('admin.requireSecretKeyLabel')"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card class="settings-section" flat outline>
        <v-card-title class="settings-section-header">
          <h5 class="settings-section-title">{{ $t('admin.profileSection') }}</h5>
          <p class="settings-section-desc">{{ $t('admin.profileSectionDesc') }}</p>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-switch
                  v-model="form.lock_full_name_edit"
                  :label="$t('admin.lockFullNameEditLabel')"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card class="settings-section" flat outline>
        <v-card-title class="settings-section-header">
          <h5 class="settings-section-title">{{ $t('sidebar.orders') }}</h5>
          <p class="settings-section-desc">{{ $t('admin.ordersSectionDesc') }}</p>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 sm12 md6>
                <v-switch
                  v-model="form.allow_canceled_order_status_change"
                  :label="$t('admin.allowChangingCanceledStatusLabel')"
                ></v-switch>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-switch
                  v-model="form.auto_generate_shipping_invoice"
                  :label="$t('admin.autoCreateShippingInvoicesLabel')"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card class="settings-section" flat outline>
        <v-card-title class="settings-section-header">
          <h5 class="settings-section-title">{{ $t('admin.onlinePaymentSection') }}</h5>
          <p class="settings-section-desc">
            {{ $t('admin.onlinePaymentSectionDesc') }}
          </p>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-switch
                  v-model="form.online_payment_enabled"
                  :label="$t('admin.enableOnlinePaymentLabel')"
                ></v-switch>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-text-field
                  style="width: 90%"
                  :label="$t('admin.fibCommissionRateLabel')"
                  :hint="$t('admin.fibCommissionRateHint')"
                  persistent-hint
                  :error-messages="errors.collect('FIB commission rate')"
                  v-validate="'required|decimal:2'"
                  data-vv-name="FIB commission rate"
                  v-model.number="form.fib_commission_rate"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-text-field
                  style="width: 90%"
                  :label="$t('admin.qiCommissionRateLabel')"
                  :hint="$t('admin.qiCommissionRateHint')"
                  persistent-hint
                  :error-messages="errors.collect('QI Card commission rate')"
                  v-validate="'required|decimal:2'"
                  data-vv-name="QI Card commission rate"
                  v-model.number="form.qi_commission_rate"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card class="settings-section" flat outline>
        <v-card-title class="settings-section-header">
          <h5 class="settings-section-title">{{ $t('admin.staleOrdersSection') }}</h5>
          <p class="settings-section-desc">
            {{ $t('admin.staleOrdersSectionDesc') }}
          </p>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 sm12 md6>
                <v-switch
                  v-model="form.auto_cancel_stale_orders_enabled"
                  :label="$t('admin.autoCancelStaleOrdersLabel')"
                ></v-switch>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-text-field
                  style="width: 90%"
                  :label="$t('admin.hoursBeforeAutoCancelLabel')"
                  :hint="$t('admin.hoursBeforeAutoCancelHint')"
                  persistent-hint
                  :error-messages="errors.collect('Hours before auto-cancel')"
                  v-validate="'required|integer|min_value:1'"
                  data-vv-name="Hours before auto-cancel"
                  v-model.number="form.auto_cancel_stale_orders_after_hours"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-switch
                  v-model="form.notify_unapproved_orders_enabled"
                  :label="$t('admin.notifyProcessedOrdersLabel')"
                ></v-switch>
              </v-flex>
              <v-flex xs12 sm12 md6>
                <v-text-field
                  style="width: 90%"
                  :label="$t('admin.hoursBeforeReminderLabel')"
                  :hint="$t('admin.hoursBeforeReminderHint')"
                  persistent-hint
                  :error-messages="errors.collect('Hours before reminder notification')"
                  v-validate="'required|integer|min_value:1'"
                  data-vv-name="Hours before reminder notification"
                  v-model.number="form.notify_unapproved_orders_after_hours"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-spacer></v-spacer>
      <v-flex
        v-if="$_has_perm('edit_settings')"
        xs12
        sm12
        md5
      >
        <v-btn type="submit" style="width: 95%; height:auto; margin-bottom: 3%" color="success"
          >{{ $t('common.save') }}</v-btn
        >
      </v-flex>
    </form>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  created() {
    let thePanel = document.querySelector('.panel-flat')
    if (thePanel) thePanel.style.overflowX = 'hidden'
  },

  mounted() {
    this.getSettings()
  },

  $_veeValidate: {
    validator: 'new'
  },

  data() {
    return {
      settingsUrl: 'api/settings',
      successAlert: false,
      errorAlert: false,
      form: {
        secret_key_enabled: true,
        lock_full_name_edit: false,
        allow_canceled_order_status_change: false,
        auto_generate_shipping_invoice: false,
        online_payment_enabled: true,
        fib_commission_rate: 0,
        qi_commission_rate: 0,
        auto_cancel_stale_orders_enabled: true,
        auto_cancel_stale_orders_after_hours: 168,
        notify_unapproved_orders_enabled: false,
        notify_unapproved_orders_after_hours: 24
      }
    }
  },

  methods: {
    getSettings() {
      let token = localStorage.getItem('access_token')

      axios
        .get(this.settingsUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then(response => {
          this.form.secret_key_enabled = response.data.data.secret_key_enabled
          this.form.lock_full_name_edit = response.data.data.lock_full_name_edit
          this.form.allow_canceled_order_status_change = response.data.data.allow_canceled_order_status_change
          this.form.auto_generate_shipping_invoice = response.data.data.auto_generate_shipping_invoice
          this.form.online_payment_enabled = response.data.data.online_payment_enabled
          this.form.fib_commission_rate = response.data.data.fib_commission_rate || 0
          this.form.qi_commission_rate = response.data.data.qi_commission_rate || 0
          this.form.auto_cancel_stale_orders_enabled = response.data.data.auto_cancel_stale_orders_enabled
          this.form.auto_cancel_stale_orders_after_hours = response.data.data.auto_cancel_stale_orders_after_hours || 168
          this.form.notify_unapproved_orders_enabled = response.data.data.notify_unapproved_orders_enabled
          this.form.notify_unapproved_orders_after_hours = response.data.data.notify_unapproved_orders_after_hours || 24
        })
    },

    submitForm() {
      this.$validator.validateAll().then(res => {
        if (!res) return

        let token = localStorage.getItem('access_token')

        axios
          .put(this.settingsUrl + '/' + 1, this.form, {
            headers: { Authorization: 'Bearer ' + token }
          })
          .then(response => {
            localStorage.setItem('app_settings', JSON.stringify(this.form))
            this.errorAlert = false
            this.successAlert = true

            let self = this
            setTimeout(() => {
              self.successAlert = false
            }, 2000)
          })
          .catch(() => {
            this.successAlert = false
            this.errorAlert = true

            let self = this
            setTimeout(() => {
              self.errorAlert = false
            }, 2000)
          })
      })
    }
  }
}
</script>

<style scoped>
.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.settings-section {
  margin-bottom: 16px;
}

.settings-section-header {
  flex-direction: column;
  align-items: flex-start;
}

.settings-section-title {
  margin: 0;
  font-weight: 500;
}

.settings-section-desc {
  margin: 4px 0 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
}
</style>



// WEBPACK FOOTER //
// src/components/settings/settings.vue